import { Link } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { extendTrial, fetchAccounts, impersonateTenant, setAccountStatus, startTrial } from "@/lib/platform-api";
import { saveAuthSession } from "@/lib/auth-session";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" });
  } catch {
    return iso;
  }
}

export default function PlatformAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTrialForm, setShowTrialForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    ownerEmail: "",
    orgName: "",
    ownerName: "",
    plan: "studio",
    trialDays: 14,
    notes: "",
  });
  const [lastProvision, setLastProvision] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchAccounts();
      setAccounts(data.accounts || []);
    } catch (err) {
      toast.error("Could not load accounts", { description: err.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submitTrial = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const result = await startTrial(form);
      setLastProvision(result);
      toast.success("Trial workspace created", { description: result.tenantId });
      setShowTrialForm(false);
      setForm({ ownerEmail: "", orgName: "", ownerName: "", plan: "studio", trialDays: 14, notes: "" });
      await load();
    } catch (err) {
      toast.error("Trial failed", { description: err.message });
    } finally {
      setBusy(false);
    }
  };

  const onExtend = async (orgId) => {
    try {
      await extendTrial(orgId, 7);
      toast.success("Trial extended by 7 days");
      await load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const onViewAsTenant = async (orgId) => {
    try {
      const data = await impersonateTenant(orgId, true);
      saveAuthSession({
        token: data.token,
        email: "impersonation@platform",
        tenantId: data.tenantId,
        role: "admin",
        impersonating: true,
        readOnly: data.readOnly,
      });
      toast.success("Viewing workspace (read-only)", { description: data.tenantId });
      window.location.href = "/admin";
    } catch (err) {
      toast.error(err.message);
    }
  };

  const onSetStatus = async (orgId, status) => {
    const reason =
      typeof window !== "undefined"
        ? window.prompt(`Reason for marking account ${status}?`, "Platform ops")
        : "";
    if (reason === null) return;
    try {
      await setAccountStatus(orgId, status, reason || "");
      toast.success(`Account marked ${status}`);
      await load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="p-8 lg:p-12 max-w-[1400px] mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-overline">Platform</div>
          <h1 className="font-heading text-4xl mt-2 tracking-tight font-medium">Accounts</h1>
        </div>
        <button type="button" className="btn-primary" onClick={() => setShowTrialForm((v) => !v)}>
          {showTrialForm ? "Cancel" : "Start trial"}
        </button>
      </div>

      {showTrialForm ? (
        <form onSubmit={submitTrial} className="surface p-6 mt-8 space-y-4 max-w-xl">
          <div className="text-overline">New trial workspace</div>
          <input
            className="input-dark w-full"
            placeholder="Owner email *"
            type="email"
            required
            value={form.ownerEmail}
            onChange={(e) => setForm((p) => ({ ...p, ownerEmail: e.target.value }))}
          />
          <input
            className="input-dark w-full"
            placeholder="Organization name"
            value={form.orgName}
            onChange={(e) => setForm((p) => ({ ...p, orgName: e.target.value }))}
          />
          <input
            className="input-dark w-full"
            placeholder="Owner display name"
            value={form.ownerName}
            onChange={(e) => setForm((p) => ({ ...p, ownerName: e.target.value }))}
          />
          <div className="flex gap-3 flex-wrap">
            <select className="input-dark" value={form.plan} onChange={(e) => setForm((p) => ({ ...p, plan: e.target.value }))}>
              <option value="starter">Starter</option>
              <option value="studio">Studio</option>
              <option value="scale">Scale</option>
              <option value="enterprise">Enterprise</option>
            </select>
            <input
              type="number"
              min={1}
              max={90}
              className="input-dark w-28"
              value={form.trialDays}
              onChange={(e) => setForm((p) => ({ ...p, trialDays: Number(e.target.value) }))}
            />
          </div>
          <textarea
            className="input-dark w-full min-h-[80px]"
            placeholder="Internal notes"
            value={form.notes}
            onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
          />
          <button type="submit" className="btn-primary" disabled={busy}>
            {busy ? "Provisioning…" : "Create trial"}
          </button>
        </form>
      ) : null}

      {lastProvision?.temporaryPassword ? (
        <div className="surface p-5 mt-6 border border-[var(--primary)]/40 max-w-xl">
          <div className="text-overline text-[var(--primary)]">One-time credentials</div>
          <p className="text-sm mt-2 text-[var(--text-secondary)]">
            Share securely with the trial owner. They should reset password after first login.
          </p>
          <p className="font-mono text-sm mt-3">
            {lastProvision.ownerEmail} / {lastProvision.temporaryPassword}
          </p>
          <p className="font-mono text-xs mt-2 text-[var(--text-tertiary)]">Tenant: {lastProvision.tenantId}</p>
        </div>
      ) : null}

      <div className="mt-10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-overline border-b" style={{ borderColor: "var(--border-default)" }}>
              <th className="py-3 pr-4">Organization</th>
              <th className="py-3 pr-4">Status</th>
              <th className="py-3 pr-4">Billing</th>
              <th className="py-3 pr-4">Expiry</th>
              <th className="py-3 pr-4">Events</th>
              <th className="py-3 pr-4">Users</th>
              <th className="py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="py-8 text-[var(--text-tertiary)]">
                  Loading…
                </td>
              </tr>
            ) : (
              accounts.map((a) => (
                <tr key={a.id} className="border-b" style={{ borderColor: "var(--border-default)" }}>
                  <td className="py-4 pr-4">
                    <div className="font-medium">{a.name}</div>
                    <div className="text-xs text-[var(--text-tertiary)] font-mono">{a.id}</div>
                    <div className="text-xs text-[var(--text-secondary)]">{a.ownerEmail}</div>
                    {a.grandfathered ? (
                      <span className="inline-block mt-1 text-[10px] uppercase tracking-wider text-[var(--primary)]">
                        Live client · no expiry
                      </span>
                    ) : null}
                  </td>
                  <td className="py-4 pr-4 capitalize">{a.status}</td>
                  <td className="py-4 pr-4">{a.billingLabel || a.plan || "—"}</td>
                  <td className="py-4 pr-4">{a.grandfathered ? "Never" : formatDate(a.trialEndsAt)}</td>
                  <td className="py-4 pr-4">{a.eventCount}</td>
                  <td className="py-4 pr-4">{a.userCount}</td>
                  <td className="py-4 space-y-1">
                    <div className="flex flex-wrap gap-2">
                      <Link to={`/platform/accounts/${a.id}`} className="text-xs text-[var(--primary)] hover:underline">
                        Manage
                      </Link>
                      <button type="button" className="btn-ghost text-xs" onClick={() => onViewAsTenant(a.id)}>
                        View as tenant
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {a.status === "trial" && !a.grandfathered ? (
                        <button type="button" className="btn-ghost text-xs" onClick={() => onExtend(a.id)}>
                          +7 days
                        </button>
                      ) : null}
                      {a.status !== "suspended" ? (
                        <button type="button" className="btn-ghost text-xs text-[var(--error)]" onClick={() => onSetStatus(a.id, "suspended")}>
                          Suspend
                        </button>
                      ) : (
                        <button type="button" className="btn-ghost text-xs" onClick={() => onSetStatus(a.id, "active")}>
                          Reactivate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
